import {
  BenefitsDependentEnrollment,
  BenefitsEnrollment,
  BenefitsEnrollmentStateEnum,
} from "@oysterhr/schema"
import { useForm } from "react-hook-form"
import { SimpleMoney } from "@oysterhr/common"
import * as Yup from "yup"
import "~/shared/utils/validators/yupValidator"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { yupResolver } from "@hookform/resolvers/yup"
import { MoneyInputValue } from "@oysterhr/feature-components"
import useSchemaShapes from "../../components/EnrollmentForm/hooks/useSchemaShapes"

export type FormValues = {
  coverageStartDate: string
  price: MoneyInputValue
  employerContribution: string
}

export function useTransitionToEnrolledForm(
  enrollment: BenefitsEnrollment | BenefitsDependentEnrollment,
) {
  const existingPrice = SimpleMoney.fromNullable(enrollment.pricePaidProvider)

  const employerContributioMoney = SimpleMoney.fromNullable(
    enrollment.currentPrice?.employerContribution,
  )

  return useForm<FormValues>({
    defaultValues: {
      coverageStartDate:
        enrollment.coverageStartDate ?? enrollment.currentPrice?.effectiveDate ?? "",
      price: {
        amount: existingPrice?.amount ? String(existingPrice?.amount) : undefined,
        currency: existingPrice?.currencyCode ?? undefined,
      },
      employerContribution: String(employerContributioMoney?.amount ?? ""),
    },
    resolver: yupResolver(useValidationSchema(enrollment)),
  })
}

function useValidationSchema(enrollment: BenefitsEnrollment | BenefitsDependentEnrollment) {
  const [t] = useTranslation("benefits/TransitionToEnrolledModal")
  const shapes = useSchemaShapes()
  const employerContributionValidation = useMemo(
    () =>
      Yup.number()
        .typeError(t("error.notNumber"))
        .required(t("error.required"))
        .min(0, t("error.positive"))
        .when(["price"], (price, schema) =>
          schema.max(price.amount, t("error.contributionExceedsTotal")),
        ),
    [t],
  )

  return useMemo(
    () =>
      Yup.object().shape({
        coverageStartDate: Yup.string().trim().required(t("required")),
        price: shapes.pricePaidProviderObjectShape({
          enrollmentState: BenefitsEnrollmentStateEnum.Enrolled,
        }),
        employerContribution: enrollment.plan.costSplitAllowed
          ? employerContributionValidation
          : Yup.mixed(),
      }),
    [t, shapes, employerContributionValidation, enrollment.plan.costSplitAllowed],
  )
}
